import React from 'react';
import { motion } from 'framer-motion';
import { aboutData, testimonialsData } from '../mockData';
import { Target, Users, Lightbulb, Shield } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const About = () => {
  useSEO({
    title: 'About Us | Hei Web Creations Singapore',
    description:
      'Singapore-based web design agency merging human craft with AI-powered workflows to deliver premium websites faster and more affordably for SMEs and startups.',
  });

  const valueIcons = {
    0: Target,
    1: Users,
    2: Lightbulb,
    3: Shield,
  };

  return (
    <div className="pt-32 bg-white" data-testid="about-page">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              {aboutData.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-500">{aboutData.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {aboutData.story.split('\n\n').map((paragraph, index) => (
                <p
                  key={index}
                  className="text-xl text-gray-600 leading-relaxed mb-8"
                >
                  {paragraph}
                </p>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-32 bg-[#FAFAFA]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              Our Values
            </h2>
            <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6 }}
                  className="group bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-500 border border-gray-100"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#FAFAFA] group-hover:bg-[#C8102E] flex items-center justify-center mb-6 transition-all duration-500">
                    <Icon className="text-[#C8102E] group-hover:text-white transition-colors duration-500" size={26} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              What our clients say
            </h2>
            <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">
              Real feedback from real clients who trusted us with their digital presence
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonialsData.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#FAFAFA] rounded-3xl p-8 border border-gray-100 hover:shadow-lg transition-all duration-300"
              >
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-bold text-[#1A1A1A]">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.position}</p>
                  <p className="text-sm text-[#C8102E]">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
